// The readout line, its explainer card and the core card (§12.0, R1458,
// R1749, R1753; card 01 of mockup round 082), as pure markup.
//
// The readout line holds the five commodities with their store gauges,
// the Data Core and the world clock with the most urgent timer under it.
// A press on a cell opens its explainer card, which states what the cell
// cannot: the rate and the true store of a commodity, how a core is won,
// and the rule of the world clock with every timer that runs. The Data
// Core cell opens the core card instead, the one place a core is spent.
//
// Each function reads the state and the few readings the overlay hands it.
// The overlay writes the markup, places the cards and owns the presses.

import { JobKind } from '../../gen/proto/aquaphobia';
import type { Resources } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { type GameState, RESOURCE_NAMES, dur, num } from '../../state';
import { type Signal, type RunningTimer, timeCell } from '../alarms';
import { isFirstSession } from '../firstsession';
import { quakeLabel, seasonCountdown } from '../format';
import { hhmmss } from '../../locale/clock';
import { esc } from '../esc';
import { termButton } from '../glossary';
import { fullInSec } from '../storewait';
import { resIcon } from '../resicon';
import { keepFigure } from '../welcome';
import { coreTrade } from './datacore';
import { BAR_TERMS, RES_DEPOSIT } from './tables';
import { groupThousands } from '../../locale/group';

/**
 * What the card says about one top-bar control.
 *
 * One builder for two readers: the card renders it, and the control's
 * `title` attribute states the same lines. A desktop therefore keeps its
 * native fallback, which is also what a screen reader reads, and the two
 * cannot drift apart.
 *
 * `extra` is trusted markup and not text — the seaquake label, which
 * `format.ts` already writes with its own colour, and the sentences of
 * the timers — so the title attribute leaves it out.
 */
export interface BarTipParts {
  title: string;
  lines: string[];
  full: boolean;
  extra: string[];
}

/** The name of a commodity. RESOURCE_NAMES is indexed by deposit id, and
 *  credits are not mined, so they have no deposit and name themselves. */
export function resName(key: keyof Resources): string {
  const deposit = RES_DEPOSIT[key];
  return deposit === undefined ? S.res_credits : RESOURCE_NAMES[deposit];
}

/**
 * What the world clock is, in one sentence.
 *
 * It is the rule the readout cannot state by itself, and §8 says it must
 * survive: the word `WORLD` came off the bar in R218, so this text is the
 * only place the client says which clock the hour belongs to. The `title`
 * attribute carries it and the explainer card repeats it.
 */
function clockRule(s: GameState): string {
  return S.clock_rule({ day: dur(86400 / s.timescale) });
}

/**
 * How a player comes by a Data Core, in one line (R1458): an expedition
 * dive can bring one back, at the chance per depth the catalog states.
 * The explainer card states it, and the core card repeats it, because a
 * telephone has no hover and a tap on the cell opens the core card.
 */
function coreHowLine(s: GameState): string {
  const dives = s.catalog?.expeditions ?? [];
  if (dives.length === 0) return S.bartip_cores_how_plain;
  return S.bartip_cores_how({
    chances: dives.map((x) => S.bartip_cores_depth({ depth: x.depth, pct: x.coreChancePct }))
      .join(' · '),
  });
}

/** The card of the world clock: the rule, the season and its wall time,
 *  and the whole sentence of every timer that runs. */
export function clockTip(s: GameState, timers: readonly RunningTimer[]): BarTipParts {
  const left = s.seasonLeftSec();
  return {
    title: S.bartip_clock_title,
    lines: [
      clockRule(s),
      S.bartip_season_resets({
        season: s.board?.season ?? s.hello?.season ?? 1, left: seasonCountdown(left),
      }),
      S.bartip_world_real({
        world: dur(left), real: dur(left / s.timescale), scale: s.timescale,
      }),
    ],
    full: false,
    // The whole sentence of every timer that runs (R430, R1749). The
    // readout line names the most urgent one under the clock and
    // counts the rest as `+N`, so this card is where a player reads
    // them all: the maintenance window, the lost link, then the
    // seaquake stage while one runs or the founding truce while it
    // holds.
    extra: clockExtra(s, timers, left, s.truceLeftSec()),
  };
}

/** The sentences of the running timers, for the clock's card (R1749),
 *  in the signal rank (R1753): the seaquake states its stage name in the
 *  tone of the stage (R2090), the truce its whole sentence, and the other
 *  two the tab they carry. */
function clockExtra(s: GameState, timers: readonly RunningTimer[], left: number, truceLeft: number): string[] {
  return timers.flatMap((t) => {
    if (t.id === 'quake') {
      const card = s.settlementCard();
      const line = quakeLabel(t.stage, left, s.settlementLeftSec(), card, s.settlementHeld) ?? t.claim.sentence;
      const rule = quakeRule(t.stage, card, s.quakeNextSec());
      return rule ? [line, esc(rule)] : [line];
    }
    // A first session reads the truce as protection, on the live clock
    // (R2133).
    if (t.id === 'truce') {
      return isFirstSession()
        ? esc(S.truce_first({ left: keepFigure(dur(s.truceLeftSec())) }))
        : S.truce({ left: dur(truceLeft) });
    }
    return t.claim.sentence;
  });
}

/**
 * What a seaquake stage does, under its line in the clock's card (R2366,
 * numbers.md §15). READ of the alarm row opens this card, and the tab
 * names the stage only. Stage 1 adds the world time to the first shock,
 * which opens the war economy. The card phase and a calm sea state none.
 */
export function quakeRule(stage: number, card: boolean, nextSec: number): string {
  switch (stage) {
    case 1:
      return nextSec > 0 ? `${S.quake_rule_1} ${S.quake_rule_1_next({ next: dur(nextSec) })}` : S.quake_rule_1;
    case 2: return S.quake_rule_2;
    case 3: return card ? '' : S.quake_rule_3;
    default: return '';
  }
}

/**
 * The card of the Data Core cell (R1458): the store, how a core is won,
 * and what one does. `data_core_sec` is work at 1x (R775), so it is
 * stated as work and takes no queue rate; a server older than R776 sends
 * 0, and the card then leaves that line out.
 */
export function coreTip(s: GameState): BarTipParts {
  const e = s.empire;
  const worth = e?.dataCoreSec ?? 0;
  return {
    title: S.bartip_cores_title,
    lines: [
      S.bartip_cores_have({ have: groupThousands(e?.dataCores ?? 0) }),
      coreHowLine(s),
      ...(worth > 0 ? [S.bartip_cores_does({ work: dur(worth) })] : []),
    ],
    full: false,
    extra: [],
  };
}

/** The card of one commodity cell: its rate and its store. */
export function storeTip(s: GameState, k: keyof Resources): BarTipParts {
  const v = s.res[k];
  const cap = s.empire?.caps?.[k] ?? 0;
  const rate = s.empire?.income?.[k] ?? 0;
  const full = cap > 0 && v >= cap;
  const lines: string[] = [];
  // Every figure of this card is exact (R687). The bar cell behind it
  // compacts, because it has to fit; the card is where a player reads
  // the true store, so `10k` on the bar reads `10 403` here.
  //
  // No rate line rather than `+0/h`: isotopes are salvaged and credits are
  // earned, so neither one has an hourly figure to state, and a zero
  // would read as a production that has stopped.
  if (rate > 0) lines.push(S.rate_per_hour({ rate: groupThousands(rate) }));
  // Credits are uncapped (`caps.credits` is 0), and a cap of zero is the
  // wire's way of saying so — never a store with no room in it.
  lines.push(cap > 0
    ? S.bartip_stored({ have: groupThousands(v), cap: groupThousands(cap) })
    : S.bartip_uncapped({ have: groupThousands(v) }));
  // The time to a full store at the rate of its mines (R3258), the figure
  // that ECONOMY · PRODUCTION states on the store row.
  const left = fullInSec(v, cap, rate);
  if (left !== undefined) lines.push(S.production_full_in({ left: dur(left) }));
  return { title: resName(k), lines, full, extra: [] };
}

/** The card's own markup. A commodity's name is a term of the player
 *  glossary (R1557), so a press on it opens the definition in this card. */
export function barTipHtml(key: string, p: BarTipParts): string {
  const term = BAR_TERMS[key];
  return `<div class="title">${term ? termButton(term, p.title) : esc(p.title)}</div>`
    + p.lines.map((l) => `<div class="line">${esc(l)}</div>`).join('')
    + (p.full ? `<div class="line full">${S.bartip_full}</div>` : '')
    + p.extra.map((x) => `<div class="line">${x}</div>`).join('');
}

/** The same lines as one `title` attribute. */
function barTipTitle(p: BarTipParts): string {
  return [p.title, ...p.lines, ...(p.full ? [S.bartip_full] : [])].join(' · ');
}

/**
 * One commodity cell. A cell is a control (§12.0): it opens the explainer
 * card that names the commodity, its rate and its store. It keeps the
 * face of a readout, because the line is an instrument and not a row of
 * buttons.
 */
function resCell(s: GameState, key: keyof Resources, rate?: number): string {
  const v = s.res[key];
  const caps = s.empire?.caps;
  const cap = caps ? caps[key] : 0;
  const full = cap > 0 && v >= cap;
  // The store gauge, in whole percent so the markup moves with the
  // store and not with every unit of it. Credits are uncapped (the
  // wire states a cap of 0), so their cell draws no gauge.
  const pct = cap > 0 ? Math.min(100, Math.max(0, Math.round((v / cap) * 100))) : -1;
  return `<button class="res r-${key}${full ? ' full' : ''}"`
    + ` data-act="bartip" data-tip="${key}" title="${esc(barTipTitle(storeTip(s, key)))}">`
    + `<span class="glyph">${resIcon(key)}</span><span class="value">${num(v)}</span>`
    + (rate ? `<span class="rate">${S.rate_per_hour({ rate })}</span>` : '')
    + (pct >= 0 ? `<i class="gauge"><b style="width:${pct}%"></b></i>` : '')
    + `</button>`;
}

/**
 * The Data Core cell (R1458), right after credits. A core is no
 * commodity — it is not mined, not capped and not spent on a price — but
 * a player who holds one must see it, and its card is the one place a
 * core is spent (R1893). With no core the cell is absent, never a dead
 * control.
 *
 * A hover opens the explainer card like the five cells beside it, and a
 * press opens the core card instead (`data-act="coreask"`): the one thing
 * a player does with a core is spend it. Its icon is the drawn chip of
 * `ui/resicon.ts` (R1764), and no other bar cell uses it. `asked` lights
 * the cell while the core card stands.
 */
function coreCell(s: GameState, asked: boolean): string {
  const n = s.empire?.dataCores ?? 0;
  if (n <= 0) return '';
  return `<button class="res r-core${asked ? ' on' : ''}" data-act="coreask"`
    + ` data-tip="cores" title="${esc(barTipTitle(coreTip(s)))}">`
    + `<span class="glyph">${resIcon('core')}</span><span class="value">${num(n)}</span></button>`;
}

/**
 * The time cell of the readout line (R1749): the world clock, and under
 * it on a telephone (beside it on a desktop) the most urgent timer with
 * `+N` for the rest. A press opens the clock's card, which states every
 * running timer. A fault carries a hatched mark before its word, so it
 * reads by shape as well as by the drain line under it.
 *
 * The world clock is not the wall clock and must never be read as one: a
 * strategic object's vulnerability window opens against *this* clock.
 * The `title` and the card say so. The glyph is geometric (U+25F7), so a
 * telephone draws no colour emoji in its place. The seconds figure is in
 * the markup, so the line's change guard lets it through once a second
 * (R194).
 *
 * The timer stands here under the AUTO look of the notices (`drain`). A
 * look that draws the timer itself (R824) keeps its own lane, and the
 * cell then holds the clock alone: `drain` is false.
 *
 * The cell reads the signal rank (R1753): `+N` counts every other signal
 * of ranks 1 to 3, a siege warning included, and while two alarms or
 * more run a press opens the alarm list in place of the clock's card.
 * The cell is lit while the list stands (`alarmsOpen`). A hover on a
 * desktop still opens the clock's card, so the rule of the world clock
 * stays one gesture away.
 */
function clockCell(s: GameState, signals: readonly Signal[], drain: boolean, alarmsOpen: boolean): string {
  const cell = timeCell(signals);
  const first = drain ? cell.first : null;
  const t = first?.claim;
  const line = t
    ? `<span class="tline${t.fault ? ' fault' : ''}">${t.fault ? '<i class="hatch"></i>' : ''}`
      + `<span class="tg">${t.glyph}</span>`
      + `<span class="tw"><span class="wide">${t.label}</span><span class="narrow">${t.short}</span></span>`
      + (t.figure ? ` <b>${t.figure}</b>` : '')
      + (cell.more > 0 ? `<span class="more">+${cell.more}</span>` : '')
      + `</span>`
    : '';
  const press = cell.list
    ? `data-act="alarms" aria-haspopup="true" aria-expanded="${alarmsOpen}"` : 'data-act="bartip"';
  return `<button class="clock${t ? ' two' : ''}${alarmsOpen ? ' on' : ''}" ${press} data-tip="clock"`
    + ` title="${esc(clockRule(s))}">`
    + `<span class="rl-ck"><span class="cg">${resIcon('clock')}</span><span class="tv">${hhmmss(s.worldClockSec())}</span></span>`
    + `${line}</button>`;
}

/** What the readout line reads beside the state: the signal rank of this
 *  pass, the notices look draws the timer (`drain`), the alarm list and
 *  the core card stand. */
export interface ReadoutView {
  signals: readonly Signal[];
  drain: boolean;
  alarmsOpen: boolean;
  coreAsked: boolean;
}

/**
 * The readout line (§12.0, R1749, card 01 of mockup round 082). The two
 * groups of a desktop: the three mined commodities, then the salvaged and
 * the earned ones with the Data Core. A telephone hides the rules between
 * them and spaces the cells across the width.
 */
export function readoutHtml(s: GameState, v: ReadoutView): string {
  const inc = s.empire?.income;
  return `<span class="rl-res">`
    + resCell(s, 'alloy', inc?.alloy)
    + resCell(s, 'biomass', inc?.biomass)
    + resCell(s, 'volatiles', inc?.volatiles)
    + '<i class="rl-dv"></i>'
    + resCell(s, 'isotopes')
    + resCell(s, 'credits')
    + coreCell(s, v.coreAsked)
    + '</span><i class="rl-dv"></i>'
    + clockCell(s, v.signals, v.drain, v.alarmsOpen);
}

/**
 * The trade one core makes on the active node, as markup: the node's
 * name and the lines under it. The top-bar core card (R1458) states it.
 *
 * It is null while no research job is ACTIVE: a queued one holds no
 * build slot, and the server refuses the core. The card says so rather
 * than offer a press that is a guaranteed refusal.
 *
 * The lines: how much sooner the node lands, then the waste. More than
 * `DATA_CORE_WASTE_WARN_SHARE` of the core lost is a warning in the
 * refusal colour, and less is the plain waste line (R1458). The warning
 * does not block the press. A server that sends no figure states no
 * figure: the card then names the node alone and still asks
 * (`data_core_sec` is 0 on a server older than R776).
 */
function coreTradeHtml(s: GameState): { node: string; lines: string } | null {
  const e = s.empire;
  const job = e?.queue.find(
    (j) => j.kind === JobKind.JOB_KIND_RESEARCH && j.active,
  );
  if (!e || !job) return null;
  const t = coreTrade(e.dataCoreSec, s.jobLive(job).remainingMs / 1000);
  const world = (work: number) => dur(s.buildSec(JobKind.JOB_KIND_RESEARCH, work));
  let lines = '';
  if (t) {
    lines = `<span class="sub">${S.expedition_core_gain({ sooner: world(t.gain) })}</span>`;
    if (t.warn) {
      lines += `<span class="sub bad">${S.expedition_core_warn({
        wasted: world(t.lost), worth: world(e.dataCoreSec),
      })}</span>`;
    } else if (t.lost > 0) {
      lines += `<span class="sub">${S.expedition_core_waste({ wasted: world(t.lost) })}</span>`;
    }
  }
  return { node: esc(job.label), lines };
}

/**
 * The top-bar core card (R1458). It is the one place a core is spent
 * (R1893, which took USE CORE off FLEET · EXPEDITIONS): the question, the
 * trade `coreTradeHtml` states, the line that says how a core is won, and
 * SPEND THE CORE beside CANCEL. With no active research it states why and
 * offers CANCEL alone.
 */
export function coreCardHtml(s: GameState): string {
  const trade = coreTradeHtml(s);
  return `<div class="ask">${S.core_card_ask}</div>`
    + (trade
      ? `<div class="row"><span><span class="name">${trade.node}</span>${trade.lines}</span></div>`
      : `<div class="row"><span class="sub">${S.expedition_core_none}</span></div>`)
    + `<div class="how">${esc(coreHowLine(s))}</div>`
    + `<div class="acts">`
    + (trade ? `<button class="primary" data-act="core">${S.btn_spend_core}</button>` : '')
    + `<button data-act="coreno">${S.btn_core_cancel}</button>`
    + `</div>`;
}
